import {createMuiTheme, ThemeProvider} from '@material-ui/core/styles';
import React from 'react';
import MarkProBoldWoff2 from './fonts/MarkPro/MarkW05-Bold.woff2';
import MarkProLightWoff2 from './fonts/MarkPro/MarkW05-Light.woff2';
import MarkProRegularWoff2 from './fonts/MarkPro/MarkW05-Regular.woff2';
import HUD from './HUD';
import {OrderContextProvider} from './OrderContext';
import {ReturnsListContextProvider} from './ReturnsList/ReturnsListContext';
import {BrowserRouter} from "react-router-dom";
import LoginCheck from './components/LoginCheck';
import {Container, CssBaseline, Typography} from '@material-ui/core';
import i18n from 'i18next'
import {initReactI18next, useTranslation} from 'react-i18next';
import translations from './translations.json'
import StackTrace from "stacktrace-js";
import http from "./http";
import {ErrorBoundary, FallbackProps} from "react-error-boundary";
import GoogleAnalytics from "./GoogleAnalytics";
import DateFnsUtils from "@date-io/date-fns";
import {MuiPickersUtilsProvider} from "@material-ui/pickers";
import {de, enAU, enCA, enGB, enUS, es, fr, frCA, it, pt, ru, ja} from "date-fns/locale";

i18n
    .use(initReactI18next)
    .init({
        resources: {
            en: {translation: translations['English']},
            pt: {translation: translations['Portuguese']},
            de: {translation: translations['German']},
            fr: {translation: translations['French']},
            it: {translation: translations['Italian']},
            es: {translation: translations['Spanish']},
            ja: {translation: translations['Japanese']},
            ru: {translation: translations['Russian']}
        },
        lng: navigator.language.split('-')[0],
        fallbackLng: 'en',
        interpolation: {escapeValue: false}
    })

const getDateFnsLocale = () => {
    console.log(i18n.language)
    switch (i18n.language.toLowerCase()) {
        case 'en':
            switch (navigator.language.split('-')[1].toLowerCase()) {
                case 'us':
                    return enUS
                case 'ca':
                    return enCA
                case 'au':
                    return enAU
                default:
                    return enGB
            }
        case 'pt':
            return pt
        case 'de':
            return de
        case 'fr':
            switch (navigator.language.split('-')[1].toLowerCase()) {
                case 'ca':
                    return frCA
                default:
                    return fr
            }
        case 'it':
            return it
        case 'es':
            return es
        case 'ru':
            return ru
        case 'ja':
            return ja
        default:
            return enGB
    }
}

const theme = createMuiTheme({
    palette: {
        primary: {
            main: '#c00'
        },
        secondary: {
            main: '#f5f5f5',
            contrastText: '#645757'
        },
        background: {
            default: '#fff',
            paper: '#fff'
        }
    },
    typography: {
        fontFamily: 'MarkPro'
    },
    overrides: {
        //@ts-ignore
        MuiPaginationItem: {
            rounded: {
                borderRadius: '0'
            }
        },
        MuiTableCell: {
            head: {
                fontWeight: 'bold'
            }
        },
        MuiButton: {
            root: {
                textTransform: 'none',
                boxShadow: 'none',
                borderRadius: '0',
                padding: '5px 15px'
            },
            contained: {
                boxShadow: 'none',
                border: `rgb(0, 0, 0, 0.25) 1px solid`,
                padding: '5px 15px'
            },
            outlinedSecondary: {
                boxShadow: 'none',
                color: '#c00',
                background: '#fff',
                border: `rgb(0, 0, 0, 0.25) 1px solid`,
                padding: '5px 15px'
            },
        },
        MuiStepIcon: {
            active: {
                color: '#0f0f0f !important'
            },
            completed: {
                color: 'rgba(0, 0, 0, 0.38) !important'
            }
        },
        MuiInput: {
            root: {
                borderRadius: 0
            }
        },
        MuiOutlinedInput: {
            root: {
                borderRadius: 0
            }
        },
        MuiCard: {
            root: {
                borderRadius: 0,
                boxShadow: 'none'
            }
        },
        MuiCssBaseline: {
            '@global': {
                '@font-face': [
                    {
                        fontFamily: 'MarkPro',
                        fontStyle: 'light',
                        fontDisplay: 'swap',
                        fontWeight: 340,
                        src: `
                                local('MarkPro/MarkW05-Light.woff2'),
                                local('MarkPro'),
                                url(${MarkProLightWoff2}) format('woff2')
                            `,
                        unicodeRange:
                            'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF',
                    },
                    {
                        fontFamily: 'MarkPro',
                        fontStyle: 'normal',
                        fontDisplay: 'swap',
                        fontWeight: 440,
                        src: `
                                local('MarkPro/MarkW05-Regular.woff2'),
                                local('MarkPro'),
                                url(${MarkProRegularWoff2}) format('woff2')
                            `,
                        unicodeRange:
                            'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF',
                    },
                    {
                        fontFamily: 'MarkPro',
                        fontStyle: 'bold',
                        fontDisplay: 'swap',
                        fontWeight: 630,
                        src: `
                                local('MarkPro/MarkW05-Bold.woff2'),
                                local('MarkPro'),
                                url(${MarkProBoldWoff2}) format('woff2')
                            `,
                        unicodeRange:
                            'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF',
                    },
                ],
            },
        },
    },
})

const handleError = async (err: Error, info: { componentStack: string }) => {
    let stackTrace = ''
    let message = ''

    if (err) {
        const stack = await StackTrace.fromError(err)
        stackTrace = stack.map(s => s.toString()).join('\n\t\t')
        message = err.message
    }

    await http.post('api/Client/Log', {
        href: window.location.href,
        message,
        stackTrace,
        componentStack: info.componentStack.replaceAll('\n    ', '\n\t\t')
    })
}

const ErrorFallback: React.FC<FallbackProps> = ({error, resetErrorBoundary}) => {
    const {t} = useTranslation()

    return (
        <Container>
            <Typography variant='h4'>{t('generic_error')}</Typography>
        </Container>
    )
}

const App: React.FC = () =>
    <GoogleAnalytics>
        <BrowserRouter>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={getDateFnsLocale()}>
                <ThemeProvider theme={theme}>
                    <CssBaseline/>
                    <LoginCheck>
                        <ReturnsListContextProvider>
                            <OrderContextProvider>
                                <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
                                    <HUD/>
                                </ErrorBoundary>
                            </OrderContextProvider>
                        </ReturnsListContextProvider>
                    </LoginCheck>
                </ThemeProvider>
            </MuiPickersUtilsProvider>
        </BrowserRouter>
    </GoogleAnalytics>

export default App;
